import React, { useState, useEffect, useRef } from 'react';

import { useResizeObserver } from 'usehooks-ts';

import { Modal, Input, Table, Tag, InputNumber, Select, Splitter, Tooltip, Button, Popconfirm, Flex, Drawer } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { NotificationInstance } from 'antd/es/notification/interface';

import {
    LoadingOutlined,
    ReloadOutlined,
    CheckCircleFilled,
    CloudUploadOutlined,
    DeleteFilled,
    PlusOutlined,
    EditFilled,
} from '@ant-design/icons';

import IBaseComponentProps from '@components/base-component-props';
import Awb from '@components/awb/awb';

import PreloadingItems from '@flc/components/logistics/import/preloading/preloading-items';
import PreloadingItem from '@flc/components/logistics/import/preloading/preloading-item';
import PreloadingTotals from '@flc/components/logistics/import/preloading/preloading-totals';

import Toolbar from '@controls/toolbar/toolbar';
import Filter from '@controls/filter/filter';
import Processing from '@controls/processing';

import { exception, securityRestriction, warning } from '@extensions/notification';
import { userLoaded, setFilter } from '@store/actions';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { delayAction, getDate, getEnumList } from '@extensions/utils';

import { IUserSession } from '@entities/user-session';
import { ICountry } from '@entities/country';
import { ICity } from '@entities/city';

import { IPreloadingFilter } from '@entities/preloading-filter';
import { IPreloadingHeader } from '@entities/preloading-header';
import { IPreloadingLine } from '@entities/preloading-line';

import { Permission, hasPermission } from '@enums/permission';
import { PreloadingStatus, enumLabel as statusLabel } from '@enums/preloading-status';
import { IEnumItem } from '@enums/enum-item';

import { SumIcon } from '@icons/index';

const filterContext: string = 'ImportPreloading';

interface IImportPreloadingProps extends IBaseComponentProps {
    cities: Array<ICity>;
    countries: Array<ICountry>;
    api: NotificationInstance;
}

const ImportPreloading = (props: IImportPreloadingProps) => {
    const { countries, cities, api, serverFetch } = props;

    const initFilter: IPreloadingFilter = {
        statuses: [PreloadingStatus.Created, PreloadingStatus.Processed],
        countryIds: countries.map((c) => {
            return c.id || '';
        }),
    };

    const containerRef = useRef<HTMLDivElement>(null);
    const { width = 0, height = 0 } = useResizeObserver({
        ref: containerRef,
        box: 'border-box',
    });

    const filterRef = useRef<HTMLDivElement>(null);
    const { width: filterWidth = 0, height: filterHeight = 0 } = useResizeObserver({
        ref: filterRef,
        box: 'border-box',
    });

    const d = useAppDispatch();

    const [modal, modalCntextHolder] = Modal.useModal();

    const fileInputRef = useRef(null);

    const userSession = useAppSelector<IUserSession>((s) => s.userSession);
    const filter = useAppSelector<IPreloadingFilter>((s) => s.filters[filterContext]);

    const [headers, setHeaders] = useState<Array<IPreloadingHeader>>();
    const [selectedHeaderIds, setSelectedHeaderIds] = useState<React.Key[]>([]);
    const [currentHeader, setCurrentHeader] = useState<IPreloadingHeader>();

    const [lines, setLines] = useState<Array<IPreloadingLine>>([]);
    const [currentLine, setCurrentLine] = useState<IPreloadingLine>();

    const [importing, setImporting] = useState<boolean>(false);
    const [showFilter, setShowFilter] = useState<boolean>(true);

    const [loadingHeaders, setLoadingHeaders] = useState<boolean>(false);
    const [refreshHeadersRequired, setRefreshHeadersRequired] = useState<boolean>(false);

    const [loadingLines, setLoadingLines] = useState<boolean>(false);
    const [refreshLinesRequired, setRefreshLinesRequired] = useState<boolean>(false);

    const [openEditHeader, setOpenEditHeader] = useState<boolean>(false);
    const [openEditLine, setOpenEditLine] = useState<boolean>(false);
    const [openTotals, setOpenTotals] = useState<boolean>(false);

    const [statuses] = useState<Array<IEnumItem>>(getEnumList(PreloadingStatus, statusLabel));

    useEffect(() => {
        if (!refreshHeadersRequired) return;

        setRefreshHeadersRequired(false);
        setLoadingHeaders(true);

        serverFetch(`preloading`, { method: 'GET', queryParams: filter })
            .then((data: Array<IPreloadingHeader>) => {
                setHeaders(data);
                setLoadingHeaders(false);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка получения груза', ex, () => d(userLoaded(undefined)));
                setLoadingHeaders(false);
            });
    }, [refreshHeadersRequired]);

    useEffect(() => {
        if (!currentHeader || !refreshLinesRequired) return;

        setCurrentLine(undefined);
        setRefreshLinesRequired(false);
        setLoadingLines(true);

        const lineFilter = { ...filter };
        lineFilter.headerId = currentHeader.id;

        serverFetch(`preloading/lines`, { method: 'GET', queryParams: lineFilter })
            .then((data: Array<IPreloadingLine>) => {
                setLines(data);
                setLoadingLines(false);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка получения деталей поставки', ex, () => d(userLoaded(undefined)));
                setLoadingLines(false);
            });
    }, [currentHeader, refreshLinesRequired]);

    useEffect(() => {
        if (!filter) {
            d(setFilter(initFilter, filterContext));
            return;
        }

        delayAction(() => {
            setCurrentHeader(undefined);
            setCurrentLine(undefined);
            setLines([]);
            setSelectedHeaderIds([]);

            setRefreshHeadersRequired(true);
        });
    }, [filter]);

    const onDeleteHeader = () => {
        if (!currentHeader) return;

        serverFetch(`preloading/headers/${currentHeader.id}`, { method: 'DELETE' })
            .then(() => {
                onSelectHeaderChange([]);
                setRefreshHeadersRequired(true);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка удаления партии', ex, () => d(userLoaded(undefined)));
            });
    };

    const onDeleteLine = (id: string | undefined) => {
        if (!id) return;

        serverFetch(`preloading/lines/${id}`, { method: 'DELETE' })
            .then(() => {
                setCurrentLine(undefined);
                setRefreshHeadersRequired(true);
                setRefreshLinesRequired(true);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка удаления груза', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loadingHeaders,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setCurrentHeader(undefined);
                            setCurrentLine(undefined);
                            setLines([]);
                            setSelectedHeaderIds([]);

                            setRefreshHeadersRequired(true);
                        },
                    },
                    { isDivider: true },
                    {
                        label: 'Добавить партию',
                        key: 'add',
                        disabled: loadingHeaders,
                        icon: <PlusOutlined />,
                        onClick: () => {
                            setSelectedHeaderIds([]);
                            setCurrentHeader(undefined);
                            setCurrentLine(undefined);
                            setLines([]);
                            setOpenEditHeader(true);
                        },
                    },
                    {
                        label: 'Импорт',
                        key: 'import',
                        icon: <CloudUploadOutlined />,
                        disabled: loadingHeaders,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageTruckConsignment)) {
                                securityRestriction(api, [Permission.ManageTruckConsignment]);
                                return;
                            }

                            if (fileInputRef && fileInputRef.current) {
                                const current = fileInputRef.current as HTMLInputElement;
                                current.click();
                            }
                        },
                    },
                    { isDivider: true },
                    {
                        label: 'Итого',
                        key: 'total',
                        icon: <SumIcon style={{ fontSize: 16 }} />,
                        onClick: () => {
                            setOpenTotals(true);
                        },
                    },
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const renderLinesToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: !currentHeader || loadingLines,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshLinesRequired(true);
                        },
                    },
                    {
                        label: 'Добавить груз',
                        key: 'add',
                        disabled: !currentHeader,
                        icon: <PlusOutlined />,
                        onClick: () => {
                            setCurrentLine({
                                id: undefined,
                                headerId: currentHeader?.id,
                                countryId: currentHeader?.countryId,
                                status: PreloadingStatus.Created,
                            });
                            setOpenEditLine(true);
                        },
                    },
                ]}
            />
        );
    };

    const renderFilter = () => {
        return (
            <div ref={filterRef}>
                <Filter
                    display={showFilter}
                    items={[
                        <InputNumber
                            style={{ width: 130 }}
                            key='consigneeCode'
                            placeholder='ID клиента'
                            value={filter?.consigneeCode}
                            onChange={(value) => {
                                d(setFilter({ ...filter, consigneeCode: value }, filterContext));
                            }}
                        />,
                        <Input
                            key='markingCode'
                            style={{ width: 130 }}
                            placeholder='Маркировка'
                            value={filter?.markingCode}
                            onChange={(data) => {
                                d(setFilter({ ...filter, markingCode: data.target.value }, filterContext));
                            }}
                        />,
                        <Select
                            key='city'
                            placeholder='Город доставки'
                            value={filter?.cityIds}
                            allowClear={true}
                            size='middle'
                            mode='multiple'
                            maxTagCount='responsive'
                            style={{ width: 200 }}
                            showSearch
                            onChange={(value) => d(setFilter({ ...filter, cityIds: value }, filterContext))}
                            optionFilterProp='children'
                            filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
                            filterSort={(a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase())}
                            options={cities.map((c) => {
                                return { value: c.id, label: c.name };
                            })}
                        />,
                        <Input
                            style={{ width: 200 }}
                            key='awbNumber'
                            placeholder='AWB'
                            value={filter?.awbNumber}
                            onChange={(data: React.ChangeEvent<HTMLInputElement>) => {
                                d(setFilter({ ...filter, awbNumber: data.target.value }, filterContext));
                            }}
                        />,
                        <Select
                            key='status'
                            placeholder='Статус'
                            value={filter?.statuses}
                            size='middle'
                            mode='multiple'
                            maxTagCount='responsive'
                            allowClear
                            style={{ width: 260 }}
                            onChange={(value) => d(setFilter({ ...filter, statuses: value }, filterContext))}
                            options={statuses.map((s) => {
                                return { value: s.value, label: s.label };
                            })}
                        />,
                    ]}
                    onClear={() => d(setFilter(initFilter, filterContext))}
                />
            </div>
        );
    };

    const renderLinesTable = () => {
        const columns: ColumnsType<IPreloadingLine> = [
            {
                title: '',
                width: 60,
                render: (_, record) => {
                    return (
                        <Flex>
                            <Tooltip title='Изменить груз'>
                                <Button
                                    icon={<EditFilled />}
                                    variant='link'
                                    color='blue'
                                    onClick={() => {
                                        setCurrentLine(record);
                                        setOpenEditLine(true);
                                    }}
                                />
                            </Tooltip>
                            <Popconfirm title='Удалить выбранный груз?' onConfirm={() => onDeleteLine(record.id)}>
                                <Button icon={<DeleteFilled />} variant='link' color='blue' />
                            </Popconfirm>
                        </Flex>
                    );
                },
            },
            {
                title: '',
                width: 40,
                align: 'center',
                render: (_, record) => {
                    if (record.status == PreloadingStatus.Created)
                        return (
                            <Tooltip placement='top' title='Расчет не проведен' color='var(--main-red)'>
                                <CheckCircleFilled
                                    style={{
                                        color: 'var(--main-red)',
                                        fontSize: 18,
                                        marginTop: 5,
                                    }}
                                />
                            </Tooltip>
                        );

                    return (
                        <Tooltip
                            placement='top'
                            title={record.status == PreloadingStatus.Processed ? 'Расчет проведен' : 'Отгружено'}
                            color={record.status == PreloadingStatus.Processed ? 'var(--main-green)' : '#bfbfbf'}
                        >
                            <CheckCircleFilled
                                style={{
                                    color: record.status == PreloadingStatus.Processed ? 'var(--main-green)' : '#bfbfbf',
                                    fontSize: 18,
                                    marginTop: 5,
                                }}
                            />
                        </Tooltip>
                    );
                },
            },
            {
                title: 'ID',
                align: 'center',
                width: 80,
                dataIndex: 'consigneeCode',
            },
            {
                title: 'Маркировка',
                width: 200,
                render: (_, record) => {
                    return (
                        <Tooltip placement='topLeft' title={record.markingId ? '' : 'Маркировка не найдена'} color='var(--main-red)'>
                            <div style={{ color: record.markingId ? 'var(--main-green)' : 'var(--main-red)' }}>{record.markingCode}</div>
                        </Tooltip>
                    );
                },
            },
            {
                title: 'Город',
                width: 200,
                dataIndex: 'cityName',
            },
            {
                title: 'Кол-во / шт',
                align: 'center',
                dataIndex: 'qty',
                width: 140,
                onCell: () => ({
                    style: {
                        background: '#fafafa',
                        fontWeight: 600,
                    },
                }),
                render: (_, record) => {
                    if (record.qty) return record.qty;
                    return <span style={{ color: 'var(--main-red)', fontWeight: 600 }}>0</span>;
                },
            },
            {
                title: 'Брутто / кг',
                align: 'center',
                dataIndex: 'grossWeight',
                width: 140,
                onCell: () => ({
                    style: {
                        background: '#fafafa',
                        fontWeight: 600,
                    },
                }),
                render: (_, record) => {
                    if (record.grossWeight) return record.grossWeight;

                    if (record.countryCode == 'NL') return;

                    return <span style={{ color: 'var(--main-red)', fontWeight: 600 }}>0</span>;
                },
            },
            {
                title: 'Объемный / кг',
                align: 'center',
                dataIndex: 'volumeWeight',
                width: 140,
                onCell: () => ({
                    style: {
                        background: '#fafafa',
                        fontWeight: 600,
                    },
                }),
                render: (_, record) => {
                    if (record.volumeWeight) return record.volumeWeight;

                    if (record.countryCode == 'NL') return;

                    return <span style={{ color: 'var(--main-red)', fontWeight: 600 }}>0</span>;
                },
            },
            {
                title: 'Продукт',
                width: 190,
                align: 'center',
                render: (_, record) => {
                    return (
                        <Tag
                            color={record.itemColor ?? 'red'}
                            style={{
                                marginBottom: 2,
                                paddingBottom: 2,
                                width: 'fit-content',
                                fontSize: 15,
                                fontWeight: 600,
                            }}
                        >
                            {record.itemName ?? '- Не задан -'}
                        </Tag>
                    );
                },
            },
            {
                title: 'Планируемая дата',
                width: 140,
                align: 'center',
                render: (_, record) => {
                    if (!record.plannedDate) return;

                    return (
                        <Tag variant='solid' color='var(--main-green)' style={{ fontSize: 16 }}>
                            {getDate(record.plannedDate)}
                        </Tag>
                    );
                },
            },
            {},
        ];

        return (
            <Table
                rowKey='id'
                size='small'
                columns={columns}
                dataSource={lines}
                loading={{
                    spinning: loadingLines,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                pagination={false}
                scroll={{ y: `calc(100vh - ${showFilter ? '341px' : '298px'})`, x: 'max-content' }}
            />
        );
    };

    const onSelectHeaderChange = (selectedRowKeys: React.Key[]) => {
        setSelectedHeaderIds(selectedRowKeys);

        if (selectedRowKeys.length == 1) {
            const entity = headers?.find((o) => o.id === selectedRowKeys[0]);
            setCurrentHeader(entity);
        } else {
            setCurrentHeader(undefined);
        }

        setCurrentLine(undefined);
        setLines([]);
        setRefreshLinesRequired(true);
    };

    const renderHeadersTable = () => {
        const columns: ColumnsType<IPreloadingHeader> = [
            {
                title: '',
                width: 60,
                render: (_, record) => {
                    return (
                        <Flex>
                            <Tooltip title='Изменить партию'>
                                <Button
                                    icon={<EditFilled />}
                                    variant='link'
                                    color='blue'
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setCurrentHeader(record);
                                        setOpenEditHeader(true);
                                    }}
                                />
                            </Tooltip>
                            <Popconfirm
                                title='Удалить выбранную партию?'
                                onConfirm={(e) => {
                                    e && e.stopPropagation();
                                    onDeleteLine(record.id);
                                }}
                            >
                                <Button icon={<DeleteFilled />} variant='link' color='blue' />
                            </Popconfirm>
                        </Flex>
                    );
                },
            },
            {
                title: 'AWB',
                align: 'center',
                width: 130,
                render: (_, record) => {
                    return record.awbNumber && <Awb awbNumber={record.awbNumber} />;
                },
                fixed: 'left',
            },
            {
                title: 'Страна',
                width: 100,
                dataIndex: 'countryName',
            },
            {
                title: 'Авиа',
                width: 100,
                align: 'center',
                dataIndex: 'countryName',
                render: (_, record) => {
                    return record.cargoCarrierName && <Tag color='var(--cargo-color)'>{record.cargoCarrierName}</Tag>;
                },
            },
            {
                title: 'Кол-во / шт',
                align: 'center',
                dataIndex: 'qty',
                width: 120,
                onCell: () => ({
                    style: {
                        background: '#fafafa',
                        fontWeight: 600,
                    },
                }),
                render: (_, record) => {
                    return (
                        <>
                            <span>{record.qty} / </span>
                            <span
                                style={{ color: record.linesQty == record.qty ? 'var(--main-green)' : 'var(--main-red)', fontWeight: 600 }}
                            >
                                {record.linesQty}
                            </span>
                        </>
                    );
                },
            },
            {
                title: 'Брутто / кг',
                align: 'center',
                dataIndex: 'grossWeight',
                width: 140,
                onCell: () => ({
                    style: {
                        background: '#fafafa',
                        fontWeight: 600,
                    },
                }),
                render: (_, record) => {
                    return (
                        <>
                            <span>{record.grossWeight} / </span>
                            <span
                                style={{
                                    color: record.linesGrossWeight == record.grossWeight ? 'var(--main-green)' : 'var(--main-red)',
                                    fontWeight: 600,
                                }}
                            >
                                {record.linesGrossWeight}
                            </span>
                        </>
                    );
                },
            },
            {
                title: 'Объемный / кг',
                align: 'center',
                dataIndex: 'volumeWeight',
                width: 140,
                onCell: () => ({
                    style: {
                        background: '#fafafa',
                        fontWeight: 600,
                    },
                }),
                render: (_, record) => {
                    return (
                        <>
                            <span>{record.volumeWeight} / </span>
                            <span
                                style={{
                                    color: record.linesVolumeWeight == record.volumeWeight ? 'var(--main-green)' : 'var(--main-red)',
                                    fontWeight: 600,
                                }}
                            >
                                {record.linesVolumeWeight}
                            </span>
                        </>
                    );
                },
            },
            {},
        ];

        return (
            <Table
                rowKey='id'
                size='small'
                rowSelection={{
                    columnWidth: 32,
                    selectedRowKeys: selectedHeaderIds,
                    onChange: onSelectHeaderChange,
                    type: 'radio',
                }}
                columns={columns}
                dataSource={headers}
                loading={{
                    spinning: loadingHeaders,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                onRow={(record) => {
                    return {
                        onClick: (event) => {
                            onSelectHeaderChange([record.id || '']);
                        },
                    };
                }}
                pagination={false}
                scroll={{ y: height, x: 'max-content' }}
                summary={(pageData) => {
                    let totalQty = 0;
                    let totalLinesQty = 0;
                    let totalGrossWeight = 0;
                    let totalLinesGrossWeight = 0;
                    let totalVolumeWeight = 0;
                    let totalLinesVolumeWeight = 0;

                    pageData.forEach(({ qty, linesQty, grossWeight, linesGrossWeight, volumeWeight, linesVolumeWeight }) => {
                        totalQty += qty || 0;
                        totalLinesQty += linesQty || 0;
                        totalGrossWeight += grossWeight || 0;
                        totalLinesGrossWeight += linesGrossWeight || 0;
                        totalVolumeWeight += volumeWeight || 0;
                        totalLinesVolumeWeight += linesVolumeWeight || 0;
                    });

                    return (
                        <Table.Summary fixed>
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0} colSpan={5} align='left'>
                                    ИТОГО
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={6} align='center'>
                                    <>
                                        <span>{totalQty} / </span>
                                        <span
                                            style={{
                                                color: totalQty == totalLinesQty ? 'var(--main-green)' : 'var(--main-red)',
                                                fontWeight: 600,
                                            }}
                                        >
                                            {totalLinesQty}
                                        </span>
                                    </>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={7} align='center'>
                                    <>
                                        <span>{totalGrossWeight} / </span>
                                        <span
                                            style={{
                                                color: totalGrossWeight == totalLinesGrossWeight ? 'var(--main-green)' : 'var(--main-red)',
                                                fontWeight: 600,
                                            }}
                                        >
                                            {totalLinesGrossWeight}
                                        </span>
                                    </>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={8} align='center'>
                                    <>
                                        <span>{totalVolumeWeight} / </span>
                                        <span
                                            style={{
                                                color:
                                                    totalVolumeWeight == totalLinesVolumeWeight ? 'var(--main-green)' : 'var(--main-red)',
                                                fontWeight: 600,
                                            }}
                                        >
                                            {totalLinesVolumeWeight}
                                        </span>
                                    </>
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    );
                }}
            />
        );
    };

    return (
        <div ref={containerRef} style={{ height: `calc(100vh - 295px - ${filterHeight}px)` }}>
            {renderToolbar()}
            {renderFilter()}
            <Splitter lazy style={{ height: 'fit-content', marginLeft: '-24px' }}>
                <Splitter.Panel defaultSize='45%' min='20%' max='45%' style={{ paddingRight: 10 }}>
                    {renderHeadersTable()}
                </Splitter.Panel>
                <Splitter.Panel style={{ paddingLeft: 24 }}>
                    {renderLinesToolbar()}
                    {renderLinesTable()}
                </Splitter.Panel>
            </Splitter>

            {openEditHeader && (
                <PreloadingItems
                    headerId={currentHeader?.id}
                    baseCountries={countries}
                    api={api}
                    onSave={() => {
                        setOpenEditHeader(false);
                        setRefreshHeadersRequired(true);
                    }}
                    onCancel={() => {
                        setOpenEditHeader(false);
                    }}
                    serverFetch={serverFetch}
                />
            )}

            {currentLine && openEditLine && (
                <PreloadingItem
                    rootLine={currentLine}
                    api={api}
                    onSave={() => {
                        setOpenEditLine(false);
                        setRefreshHeadersRequired(true);
                        setRefreshLinesRequired(true);
                    }}
                    onCancel={() => {
                        setOpenEditLine(false);
                    }}
                    serverFetch={serverFetch}
                />
            )}

            {openTotals && (
                <Drawer
                    closable
                    destroyOnHidden
                    title={`Итого`}
                    placement='right'
                    open={true}
                    onClose={() => setOpenTotals(false)}
                    size={400}
                >
                    <PreloadingTotals api={api} serverFetch={serverFetch} />
                </Drawer>
            )}

            <input
                type='file'
                accept={'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}
                onChange={(x) => {
                    if (!x.target || !x.target.files) return;

                    const file = x.target.files[0];
                    if (file) {
                        const reader = new FileReader();
                        reader.readAsDataURL(file);
                        reader.onload = () => {
                            const result = reader?.result as string;

                            const fileBody = result.replace(
                                'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,',
                                '',
                            );

                            setImporting(true);
                            serverFetch(`preloading/import`, { method: 'POST', bodyData: fileBody })
                                .then((messages: any) => {
                                    setImporting(false);

                                    if (messages && messages.length > 0) {
                                        warning(
                                            api,
                                            'Файл импортирован частично',
                                            <ul style={{ overflow: 'auto', height: 300 }}>
                                                {messages.map((m: any) => {
                                                    return <li>{m}</li>;
                                                })}
                                            </ul>,
                                        );
                                    }

                                    setRefreshHeadersRequired(true);
                                })
                                .catch((ex: any) => {
                                    setImporting(false);
                                    exception(api, 'Ошибка ипорта файла', ex, () => d(userLoaded(undefined)));
                                });
                        };

                        reader.onerror = (error) => console.log(error);
                    }
                }}
                onClick={(e) => {
                    const target = e.target as HTMLInputElement;
                    target.value = '';
                }}
                multiple={false}
                ref={fileInputRef}
                style={{ display: 'none' }}
            />
            {importing && <Processing title='Импорт данных...' />}
            {modalCntextHolder}
        </div>
    );
};

export default ImportPreloading;
